import { useCallback, useEffect, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import classNames from 'classnames';
import { NavLink } from 'react-router-dom';

import type { Map as ImmutableMap } from 'immutable';

import { useAccount } from '@/mastodon/hooks/useAccount';
import AlternateEmailIcon from '@/material-icons/400-24px/alternate_email.svg?react';
import BookmarksActiveIcon from '@/material-icons/400-24px/bookmarks-fill.svg?react';
import BookmarksIcon from '@/material-icons/400-24px/bookmarks.svg?react';
import DescriptionIcon from '@/material-icons/400-24px/description.svg?react';
import EditSquareIcon from '@/material-icons/400-24px/edit_square.svg?react';
import GroupIcon from '@/material-icons/400-24px/group.svg?react';
import HomeActiveIcon from '@/material-icons/400-24px/home-fill.svg?react';
import HomeIcon from '@/material-icons/400-24px/home.svg?react';
import InfoIcon from '@/material-icons/400-24px/info.svg?react';
import KeyIcon from '@/material-icons/400-24px/key.svg?react';
import ListAltActiveIcon from '@/material-icons/400-24px/list_alt-fill.svg?react';
import ListAltIcon from '@/material-icons/400-24px/list_alt.svg?react';
import MenuIcon from '@/material-icons/400-24px/menu.svg?react';
import NotificationsActiveIcon from '@/material-icons/400-24px/notifications-fill.svg?react';
import NotificationsIcon from '@/material-icons/400-24px/notifications.svg?react';
import PersonAddActiveIcon from '@/material-icons/400-24px/person_add-fill.svg?react';
import PersonAddIcon from '@/material-icons/400-24px/person_add.svg?react';
import PublicIcon from '@/material-icons/400-24px/public.svg?react';
import SearchIcon from '@/material-icons/400-24px/search.svg?react';
import SettingsIcon from '@/material-icons/400-24px/settings.svg?react';
import StarActiveIcon from '@/material-icons/400-24px/star-fill.svg?react';
import StarIcon from '@/material-icons/400-24px/star.svg?react';
import TrendingUpIcon from '@/material-icons/400-24px/trending_up.svg?react';
import { fetchFollowRequests } from 'mastodon/actions/accounts';
import { openModal } from 'mastodon/actions/modal';
import { Avatar } from 'mastodon/components/avatar';
import type { IconProp } from 'mastodon/components/icon';
import { Icon } from 'mastodon/components/icon';
import { IconLogo } from 'mastodon/components/logo';
import { MoreLink } from 'mastodon/features/navigation_panel/components/more_link';
import { prefetchOnIntent } from 'mastodon/features/whocare_article/editor/prefetch';
import { useIdentity } from 'mastodon/identity_context';
import {
  localLiveFeedAccess,
  registrationsOpen,
  remoteLiveFeedAccess,
  sso_redirect,
  trendsEnabled,
  me,
} from 'mastodon/initial_state';
import { canViewFeed } from 'mastodon/permissions';
import { selectUnreadNotificationGroupsCount } from 'mastodon/selectors/notifications';
import { useAppDispatch, useAppSelector } from 'mastodon/store';

const messages = defineMessages({
  main: {
    id: 'navigation_bar.main',
    defaultMessage: 'Main',
    description:
      'Label for the main navigation; should not contain the word "navigation".',
  },
  home: { id: 'tabs_bar.home', defaultMessage: 'Home' },
  notifications: {
    id: 'tabs_bar.notifications',
    defaultMessage: 'Notifications',
  },
  explore: { id: 'explore.title', defaultMessage: 'Trending' },
  firehose: { id: 'column.firehose', defaultMessage: 'Live feeds' },
  firehose_singular: {
    id: 'column.firehose_singular',
    defaultMessage: 'Live feed',
  },
  lists: { id: 'navigation_bar.lists', defaultMessage: 'Lists' },
  favourites: { id: 'navigation_bar.favourites', defaultMessage: 'Favorites' },
  bookmarks: { id: 'navigation_bar.bookmarks', defaultMessage: 'Bookmarks' },
  direct: { id: 'navigation_bar.direct', defaultMessage: 'Private mentions' },
  followRequests: {
    id: 'navigation_bar.follow_requests',
    defaultMessage: 'Follow requests',
  },
  preferences: {
    id: 'navigation_bar.preferences',
    defaultMessage: 'Preferences',
  },
  search: { id: 'navigation_bar.search', defaultMessage: 'Search' },
  compose: { id: 'tabs_bar.publish', defaultMessage: 'New Post' },
  following: { id: 'threepane.following', defaultMessage: 'Following' },
  profile: { id: 'threepane.profile', defaultMessage: 'Profile' },
  writeArticle: {
    id: 'whocare_article.editor.rail_label',
    defaultMessage: 'Write an article',
  },
  more: { id: 'navigation_bar.more', defaultMessage: 'More' },
  about: { id: 'navigation_bar.about', defaultMessage: 'About' },
  signIn: { id: 'sign_in_banner.sign_in', defaultMessage: 'Login' },
  createAccount: {
    id: 'sign_in_banner.create_account',
    defaultMessage: 'Create account',
  },
  ssoRedirect: {
    id: 'sign_in_banner.sso_redirect',
    defaultMessage: 'Login or Register',
  },
  expand: { id: 'threepane.expand_nav', defaultMessage: 'Expand navigation' },
  collapse: {
    id: 'threepane.collapse_nav',
    defaultMessage: 'Collapse navigation',
  },
});

interface RailTip {
  label: string;
  top: number;
  edge: number;
  rtl: boolean;
}

const RailLink: React.FC<{
  to: string;
  label: string;
  icon: IconProp;
  activeIcon?: IconProp;
  isActive?: (match: unknown, location: { pathname: string }) => boolean;
  badge?: React.ReactNode;
  innerRef?: React.Ref<HTMLAnchorElement>;
}> = ({ to, label, icon, activeIcon, isActive, badge, innerRef }) => (
  <NavLink
    to={to}
    innerRef={innerRef}
    className={classNames('threepane-rail__link', {
      'has-active-icon': !!activeIcon,
    })}
    activeClassName='active'
    isActive={isActive}
    aria-label={label}
    data-tooltip={label}
  >
    <Icon id='' icon={icon} className='threepane-rail__icon' />
    {activeIcon && (
      <Icon
        id=''
        icon={activeIcon}
        className='threepane-rail__icon threepane-rail__icon--active'
      />
    )}
    <span className='threepane-rail__label'>{label}</span>
    {badge}
  </NavLink>
);

const RailButton: React.FC<{
  label: string;
  icon: IconProp;
  onClick: () => void;
  className?: string;
  active?: boolean;
  expanded?: boolean;
}> = ({ label, icon, onClick, className, active, expanded }) => (
  <button
    type='button'
    className={classNames('threepane-rail__link', className, { active })}
    onClick={onClick}
    aria-label={label}
    aria-expanded={expanded}
    data-tooltip={label}
  >
    <Icon id='' icon={icon} className='threepane-rail__icon' />
    <span className='threepane-rail__label'>{label}</span>
  </button>
);

const RailAnchor: React.FC<{
  href: string;
  label: string;
  icon: IconProp;
  className?: string;
  post?: boolean;
}> = ({ href, label, icon, className, post }) => (
  <a
    className={classNames('threepane-rail__link', className)}
    href={href}
    data-method={post ? 'post' : undefined}
    aria-label={label}
    data-tooltip={label}
  >
    <Icon id='' icon={icon} className='threepane-rail__icon' />
    <span className='threepane-rail__label'>{label}</span>
  </a>
);

/** What a visitor who is not signed in can do from the rail: log in or register. */
const GuestActions: React.FC = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const signupUrl = useAppSelector(
    (state) => state.server.server.item?.registrations.url ?? '/auth/sign_up',
  );
  const handleClosedRegistrations = useCallback(() => {
    dispatch(openModal({ modalType: 'CLOSED_REGISTRATIONS', modalProps: {} }));
  }, [dispatch]);

  if (sso_redirect) {
    return (
      <li>
        <RailAnchor
          href={sso_redirect}
          post
          label={intl.formatMessage(messages.ssoRedirect)}
          icon={KeyIcon}
          className='threepane-rail__compose'
        />
      </li>
    );
  }

  return (
    <>
      <li>
        <RailAnchor
          href='/auth/sign_in'
          label={intl.formatMessage(messages.signIn)}
          icon={KeyIcon}
        />
      </li>
      <li>
        {registrationsOpen ? (
          <RailAnchor
            href={signupUrl}
            label={intl.formatMessage(messages.createAccount)}
            icon={PersonAddIcon}
            className='threepane-rail__compose'
          />
        ) : (
          <RailButton
            className='threepane-rail__compose'
            label={intl.formatMessage(messages.createAccount)}
            icon={PersonAddIcon}
            onClick={handleClosedRegistrations}
          />
        )}
      </li>
    </>
  );
};

const Badge: React.FC<{ count: number }> = ({ count }) =>
  count > 0 ? (
    <span className='threepane-rail__badge'>{count > 99 ? '99+' : count}</span>
  ) : null;

const NotificationsLink: React.FC = () => {
  const intl = useIntl();
  const count = useAppSelector(selectUnreadNotificationGroupsCount);

  return (
    <RailLink
      to='/notifications'
      label={intl.formatMessage(messages.notifications)}
      icon={NotificationsIcon}
      activeIcon={NotificationsActiveIcon}
      badge={<Badge count={count} />}
    />
  );
};

const FollowRequestsLink: React.FC = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const count = useAppSelector(
    (state) =>
      (
        state.user_lists.getIn(['follow_requests', 'items']) as
          | ImmutableMap<string, unknown>
          | undefined
      )?.size ?? 0,
  );

  useEffect(() => {
    dispatch(fetchFollowRequests());
  }, [dispatch]);

  if (count === 0) {
    return null;
  }

  return (
    <RailLink
      to='/follow_requests'
      label={intl.formatMessage(messages.followRequests)}
      icon={PersonAddIcon}
      activeIcon={PersonAddActiveIcon}
      badge={<Badge count={count} />}
    />
  );
};

const isFirehoseActive = (
  match: unknown,
  { pathname }: { pathname: string },
) => !!match || pathname.startsWith('/public');

export const IconRail: React.FC<{
  onCompose: () => void;
  onSearch: () => void;
  onFollowing: () => void;
  composeActive: boolean;
  followingActive: boolean;
  expanded: boolean;
  onToggle: () => void;
}> = ({
  onCompose,
  onSearch,
  onFollowing,
  composeActive,
  followingActive,
  expanded,
  onToggle,
}) => {
  const intl = useIntl();
  const [tip, setTip] = useState<RailTip | null>(null);

  // Hover labels for the collapsed rail. They are drawn outside the rail (fixed position) because the
  // scrolling menu inside it would clip anything that sticks out.
  const showTip = useCallback((target: EventTarget) => {
    const element =
      target instanceof Element
        ? target.closest<HTMLElement>('[data-tooltip]')
        : null;

    if (!element?.dataset.tooltip) {
      setTip(null);
      return;
    }

    const rect = element.getBoundingClientRect();
    const rail = element.closest('nav')?.getBoundingClientRect() ?? rect;
    const rtl = document.documentElement.dir === 'rtl';

    setTip({
      label: element.dataset.tooltip,
      top: rect.top + rect.height / 2,
      edge: rtl ? window.innerWidth - rail.left + 6 : rail.right + 6,
      rtl,
    });
  }, []);

  const hideTip = useCallback(() => {
    setTip(null);
  }, []);

  const handlePointer = useCallback(
    (e: React.SyntheticEvent) => {
      showTip(e.target);
    },
    [showTip],
  );
  const { signedIn, permissions } = useIdentity();
  const account = useAccount(me);
  const liveFeedAllowed =
    canViewFeed(signedIn, permissions, localLiveFeedAccess) ||
    canViewFeed(signedIn, permissions, remoteLiveFeedAccess);

  return (
    <nav
      className={classNames('threepane-rail', {
        'threepane-rail--expanded': expanded,
      })}
      aria-label={intl.formatMessage(messages.main)}
      onMouseOver={expanded ? undefined : handlePointer}
      onFocus={expanded ? undefined : handlePointer}
      onMouseLeave={hideTip}
      onBlur={hideTip}
      onClickCapture={hideTip}
      onScrollCapture={hideTip}
    >
      {tip && !expanded && (
        <div
          className='threepane-rail__tip'
          role='presentation'
          style={{
            top: tip.top,
            ...(tip.rtl ? { right: tip.edge } : { left: tip.edge }),
          }}
        >
          {tip.label}
        </div>
      )}

      <div className='threepane-rail__head'>
        <NavLink
          to='/'
          className='threepane-rail__logo'
          exact
          aria-label='whocare'
        >
          <IconLogo />
        </NavLink>

        <RailButton
          className='threepane-rail__toggle'
          label={intl.formatMessage(
            expanded ? messages.collapse : messages.expand,
          )}
          icon={MenuIcon}
          onClick={onToggle}
          expanded={expanded}
        />
      </div>

      {signedIn && (
        <RailButton
          className='threepane-rail__compose'
          label={intl.formatMessage(messages.compose)}
          icon={EditSquareIcon}
          onClick={onCompose}
          active={composeActive}
        />
      )}
      {signedIn && (
        <RailLink
          to='/write'
          label={intl.formatMessage(messages.writeArticle)}
          icon={DescriptionIcon}
          innerRef={prefetchOnIntent}
        />
      )}
      <RailButton
        label={intl.formatMessage(messages.search)}
        icon={SearchIcon}
        onClick={onSearch}
      />
      {signedIn && (
        <RailButton
          label={intl.formatMessage(messages.following)}
          icon={GroupIcon}
          onClick={onFollowing}
          active={followingActive}
        />
      )}

      <div className='threepane-rail__sep' role='separator' />

      <ul className='threepane-rail__menu'>
        {signedIn && (
          <>
            <li>
              <RailLink
                to='/home'
                label={intl.formatMessage(messages.home)}
                icon={HomeIcon}
                activeIcon={HomeActiveIcon}
              />
            </li>
            <li>
              <NotificationsLink />
            </li>
            <li>
              <FollowRequestsLink />
            </li>
          </>
        )}
        {trendsEnabled && (
          <li>
            <RailLink
              to='/explore'
              label={intl.formatMessage(messages.explore)}
              icon={TrendingUpIcon}
            />
          </li>
        )}
        {liveFeedAllowed && (
          <li>
            <RailLink
              to={
                canViewFeed(signedIn, permissions, localLiveFeedAccess)
                  ? '/public/local'
                  : '/public/remote'
              }
              label={intl.formatMessage(
                canViewFeed(signedIn, permissions, localLiveFeedAccess) &&
                  canViewFeed(signedIn, permissions, remoteLiveFeedAccess)
                  ? messages.firehose
                  : messages.firehose_singular,
              )}
              icon={PublicIcon}
              isActive={isFirehoseActive}
            />
          </li>
        )}
        {signedIn ? (
          <>
            <li>
              <RailLink
                to='/lists'
                label={intl.formatMessage(messages.lists)}
                icon={ListAltIcon}
                activeIcon={ListAltActiveIcon}
              />
            </li>
            <li>
              <RailLink
                to='/bookmarks'
                label={intl.formatMessage(messages.bookmarks)}
                icon={BookmarksIcon}
                activeIcon={BookmarksActiveIcon}
              />
            </li>
            <li>
              <RailLink
                to='/favourites'
                label={intl.formatMessage(messages.favourites)}
                icon={StarIcon}
                activeIcon={StarActiveIcon}
              />
            </li>
            <li>
              <RailLink
                to='/conversations'
                label={intl.formatMessage(messages.direct)}
                icon={AlternateEmailIcon}
              />
            </li>
          </>
        ) : (
          <li>
            <RailLink
              to='/about'
              label={intl.formatMessage(messages.about)}
              icon={InfoIcon}
            />
          </li>
        )}
      </ul>

      <div className='threepane-rail__spacer' />

      <ul className='threepane-rail__menu threepane-rail__menu--bottom'>
        {signedIn ? (
          <>
            {account && (
              <li>
                <NavLink
                  to={`/@${account.acct}`}
                  exact
                  className='threepane-rail__link threepane-rail__link--profile'
                  activeClassName='active'
                  aria-label={intl.formatMessage(messages.profile)}
                  data-tooltip={intl.formatMessage(messages.profile)}
                >
                  <Avatar account={account} size={28} />
                  <span className='threepane-rail__label'>
                    {intl.formatMessage(messages.profile)}
                  </span>
                </NavLink>
              </li>
            )}
            <li>
              <a
                className='threepane-rail__link'
                href='/settings/preferences'
                aria-label={intl.formatMessage(messages.preferences)}
                data-tooltip={intl.formatMessage(messages.preferences)}
              >
                <Icon id='' icon={SettingsIcon} className='threepane-rail__icon' />
                <span className='threepane-rail__label'>
                  {intl.formatMessage(messages.preferences)}
                </span>
              </a>
            </li>
            <li
              className='threepane-rail__more'
              data-tooltip={intl.formatMessage(messages.more)}
            >
              <MoreLink />
            </li>
          </>
        ) : (
          <GuestActions />
        )}
      </ul>
    </nav>
  );
};
